import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { LocationProvider } from '@reach/router';
import styled, { css } from 'styled-components';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import devices from '../../../../../js/styles/Devices';
import ExtraFormFunctionalityWrapper from '../../ExtraFormFunctionalityWrapper';
import Form from '../../../../common/Form';
import NewFormBlock from '../../../NewFormBlock';
import Disclaimer from '../../../../utils/Disclaimer/Disclaimer';

const Wrapper = styled.div`
  ${(props) => props.leadForm && css`
    margin: 1rem auto;
    max-width: 55rem;
    min-height: 800px;
    @media ${devices.downFromTablet} {
      min-height: 1200px;
    }
  `}
`;

const FormBlockWrap = styled.div`
  && {
    position: relative;
    text-align: left;
    padding-top: 0;
    background-color: var(--form-background);
    border-radius: 1rem;
    box-shadow: var(--form-shadow);
    margin: 0 auto;

    @media ${devices.upFromTablet} {
      width: ${(props) => props.frameWidth};
    }

    ${(props) => props.type === 'loanCalculator' && css`
      max-width: 46rem;
    `}
  }
`;

const FormFrame = styled.div`
  && {
    @media ${devices.upFromTablet} {
      width: ${(props) => props.frameWidth};
    }

    @media ${devices.downFromTablet} {
      width: 100%;
    }

    ${(props) => props.type === 'loanCalculator' && css`

      @media ${devices.smallDesktop} {
        width: 30rem;
      }
    `}
  }
`;

const FormBlock = ({
  show,
  disclaimer,
  forwardRef,
  exampleLoan,
  title,
  partnerForm,
  prefilledApplicationForm,
  prefilledValues,
  subtitle,
  type,
  aboveFold,
  ...rest
}) => {
  const [showDisclaimers, setShowDisclaimers] = useState(false);
  const isFeatureEnabled = useFeatureIsOn('new-onb20');

  useEffect(() => {
    const timeout = setTimeout(() => {
      setShowDisclaimers(true);
    }, 0);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.hj && isFeatureEnabled) {
      window.hj('event', 'new_form');
    }
  }, [isFeatureEnabled]);

  if (!show) return null;

  const leadForm = type[0] === 'lead' || type[0] === 'loanCalculator';

  const renderForm = (context) => (
    <Form
      key="form-block"
      exampleLoan={exampleLoan}
      title={title}
      subtitle={subtitle}
      location={context.location}
      partnerForm={partnerForm}
      prefilledApplicationForm={prefilledApplicationForm}
      prefilledValues={prefilledValues}
      type={type[0]}
      {...rest}
    />
  );

  const formComponent = (
    <>
      {isFeatureEnabled ? (
        <NewFormBlock prefilledValuesNewForm />
      ) : (
        <>
          <FormBlockWrap
            ref={forwardRef}
            id="form"
            frameWidth={leadForm ? '55rem' : '70rem'}
            type={type}
          >
            <FormFrame
              frameWidth={leadForm ? '55rem' : '70rem'}
              type={type}
            >
              <ExtraFormFunctionalityWrapper>
                <LocationProvider>
                  {(context) => renderForm(context)}
                </LocationProvider>
              </ExtraFormFunctionalityWrapper>
            </FormFrame>
          </FormBlockWrap>
          {leadForm && showDisclaimers && (
          <Disclaimer
            isAboveFold={aboveFold}
            margin={prefilledApplicationForm ? '1rem auto' : 'auto'}
          />
          )}
        </>
      )}
    </>
  );

  return (
    <Wrapper isFeatureEnabled={isFeatureEnabled}>
      {formComponent}
    </Wrapper>
  );
};

FormBlock.propTypes = {
  show: PropTypes.bool,
  aboveFold: PropTypes.bool,
  disclaimer: PropTypes.string,
  inputSource: PropTypes.string,
  advisaSource: PropTypes.string,
  forwardRef: PropTypes.shape(),
  exampleLoan: PropTypes.shape(),
  title: PropTypes.string,
  partnerForm: PropTypes.bool,
  prefilledApplicationForm: PropTypes.bool,
  prefilledValues: PropTypes.shape(),
  subtitle: PropTypes.string,
  type: PropTypes.arrayOf(PropTypes.string),
};

FormBlock.defaultProps = {
  show: true,
  type: [''],
  disclaimer: null,
  inputSource: '',
  advisaSource: '',
  forwardRef: null,
  exampleLoan: null,
  title: null,
  partnerForm: false,
  aboveFold: false,
  prefilledApplicationForm: false,
  prefilledValues: {},
  subtitle: null,
};

export default FormBlock;

export const formBlockQuery = graphql`
  fragment formBlockFields on Form {
    show
    type
    title
    subtitle
    type
    inputSource
    advisaSource
  }
`;
